import { useState } from "react";
import type { LinkedCompany } from "@freightsimple/generated-dashboard-openapi-client/models/LinkedCompany";
import { BuildingsIcon } from "@phosphor-icons/react/Buildings";
import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ChoiceRows } from "@freightsimple/ui-core/Controls/ChoiceRows";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { CurrencyTag } from "@/Shared/Components/CurrencyTag";

const GENERIC_FAILURE = "We couldn't switch organizations. Please try again.";

export interface SwitchOrganizationDialogProps {
  onFinished: () => void;
  linkedOrganizations: LinkedCompany[];
  onSwitchOrganization: (organizationId: string) => Promise<{
    success: boolean;
    errorMessage?: string | undefined;
  }>;
}

export const AuthenticationSwitchOrganizationDialog = ({
  onFinished,
  linkedOrganizations,
  onSwitchOrganization,
}: SwitchOrganizationDialogProps) => {
  const [selectedOrganization, setSelectedOrganization] = useState<
    string | undefined
  >(undefined);

  const [isLoading, setIsLoading] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );

  const handleOrganizationSelect = (orgId: string) => {
    setSelectedOrganization(orgId);
    // Picking a different organization retires the last failure — it was about
    // the one you just moved off.
    setErrorMessage(undefined);
  };

  const handleConfirm = async () => {
    if (!selectedOrganization) return;

    setErrorMessage(undefined);

    try {
      setIsLoading(() => true);
      const result = await onSwitchOrganization(selectedOrganization);

      if (result.success) {
        onFinished();
      } else {
        // A failed switch has to say so. Without this the spinner just stops
        // and the dialog sits there looking like nothing happened.
        setErrorMessage(GENERIC_FAILURE);
      }
    } catch (error) {
      console.error("Error switching organization:", error);
      setErrorMessage(GENERIC_FAILURE);
    } finally {
      setIsLoading(() => false);
    }
  };

  // Sort organizations to show current company first
  const sortedOrganizations = [...linkedOrganizations].sort((a, b) => {
    if (a.isCurrentCompany) return -1;
    if (b.isCurrentCompany) return 1;
    return 0;
  });

  return (
    <Stack gap={8}>
      <T size="base" color="secondary">
        Click on the organization you want to switch to. You can only switch to
        organizations you have access to.{" "}
        <Button size="link" inheritFont href="https://help.freightsimple.com">
          Learn more
        </Button>
      </T>

      <ChoiceRows
        value={selectedOrganization}
        onChange={handleOrganizationSelect}
        maxHeight={500}
        options={sortedOrganizations.map((organization) => ({
          value: organization.companyId,
          icon: BuildingsIcon,
          // The organization you're already in is shown but unpickable —
          // there's nothing to switch to.
          disabled: organization.isCurrentCompany,
          content: (
            <Stack gap={0} align="start">
              <Row gap={2}>
                <T weight="medium">{organization.companyName}</T>
                <CurrencyTag currency={organization.currency} />
              </Row>
              {organization.isCurrentCompany && (
                <T size="xs" color="muted">
                  Current Organization
                </T>
              )}
            </Stack>
          ),
        }))}
      />

      {errorMessage !== undefined && (
        <Banner variant="danger">{errorMessage}</Banner>
      )}

      <Row gap={6} justify="center">
        <Button onClick={onFinished}>Cancel</Button>
        <Button
          primary
          onClick={handleConfirm}
          loading={isLoading}
          data-testid="confirm-switch-organization-button"
        >
          Confirm
        </Button>
      </Row>
    </Stack>
  );
};
